<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { ConfigModule, ModuleCategory } from '~/types/config';
import { useI18n } from 'vue-i18n';
import { useFocusNext } from '~/composables/forms/useFocusNext';
import { useAccordionTabs, type AccordionGroup } from '~/composables/useAccordionTabs';
import { useConfigParserStore } from '~/stores/configParser';
import { useToast } from 'primevue/usetoast';
import { DeleteCategories } from '~/Common/DeleteCategories';
import ConfigFormHeadlines from '~/components/base/forms/ConfigFormHeadlines.vue';

const { t } = useI18n();


const toast = useToast();

const props = withDefaults(defineProps<{
  configData: Record<string, ConfigModule>;
  focusNextId?: string;
  deleteCategories?: DeleteCategories;
  showTitle?: boolean;
  showDeleteButton?: boolean;
  profileSettings?: boolean;
  readOnly?: boolean;
}>(), {
  showDeleteButton: true,
  showTitle: true,
  readOnly: false,
});


// Emit for focusing next element
const emit = defineEmits<{
  'update:focusNextId': [value: string];
  'reload-config': [{ resolve: () => void; reject: (error: Error) => void }];
}>();

const configParserStore = useConfigParserStore();

const { focusNext, getNextInputId } = useFocusNext();

// Use the passed deleteCategories instance
const deleteCategories = props.deleteCategories;

// Active Tab for the TabView
const activeTab = ref(0);
const deleteLoading = ref(false);

const availableTabs = computed(() => configParserStore.getAvailableTabs);

// Transform availableTabs to AccordionGroup format for the composable
const accordionGroups = computed(() =>
  availableTabs.value.map(category => ({
    id: category.categoryName,
    items: category.headlineGroups || [],
    hidden: category.hidden
  }) as AccordionGroup)
);

// Use accordion composable with the transformed data
const {
  openAccordionTabs,
  resetAccordionTabs,
  handleAccordionChange,
  initializeWithDelay,
  isInitialized
} = useAccordionTabs(accordionGroups);

// Compute the selected module name with capitalized first letter
const selectedModuleName = computed(() => {
  if (!configParserStore.getSelectedModule) return '';

  const moduleData = configParserStore.getModule(configParserStore.getSelectedModule);
  return moduleData?.translation;
});

// Reset when configData changes
watch(() => props.configData, () => {
  activeTab.value = 0;
}, { deep: true });

// Watch for changes in selected module and reset active tab to first tab
watch(() => configParserStore.getSelectedModule, () => {
  activeTab.value = 0;
  // Reset all accordion tabs to be open when module changes
  resetAccordionTabs();
}, { immediate: true });

// Watch for changes in deleted categories and update accordion tabs
watch(
  () => props.deleteCategories?.deletedModulesRef?.value,
  () => {
    // Initialize accordion tabs when deletions change
    initializeWithDelay();
  },
  { deep: true, immediate: true }
);

// Watch for availableTabs changes to ensure reactivity
watch(() => availableTabs.value, (newTabs, oldTabs) => {
  // Force re-initialization of accordion tabs when tabs change
  if (JSON.stringify(newTabs) !== JSON.stringify(oldTabs)) {
    nextTick(() => {
      initializeWithDelay();
    });
  }
}, { immediate: true, deep: true });


/**
 * Handle focusing the next element by emitting to parent
 * @param nextElementId ID of the next element to focus
 */
const handleFocusNext = (nextElementId: string) => {
  // If element exists, focus the element, otherwise emit it to parent component
  if (document.getElementById(nextElementId)) {
    focusNext(nextElementId);
  } else {
    emit('update:focusNextId', nextElementId);
  }
}

/**
 * Marks a tab as deleted locally without actually deleting it from backend.
 * @param xpath - The XPath of the tab to mark as deleted.
 * @param categoryName - The display name of the category.
 */
const markTabAsDeleted = (xpath: string, categoryName: string) => {
  if (!xpath || xpath.trim() === '') {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.deleteError'),
      life: 3000
    });
    return;
  }

  // Get module and category information
  const moduleData = configParserStore.getModule(configParserStore.getSelectedModule);
  const category = moduleData ?
    Object.values(moduleData.categories).find((cat: ModuleCategory) => cat.xpath === xpath) : null;

  if (!category || !moduleData) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.deleteError'),
      life: 3000
    });
    return;
  }

  // Use DeleteCategories to mark as deleted
  const success = deleteCategories?.markCategoryAsDeleted(category, {
    moduleId: moduleData.moduleId,
    moduleName: moduleData.moduleName,
    translation: moduleData.translation,
    xpath: moduleData.xpath
  } as ConfigModule);

  if (success) {
    toast.add({
      severity: 'info',
      summary: t('common.toast.info'),
      detail: t('config.toast.markedForDeletion', { category: categoryName }),
      life: 3000
    });
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.deleteError'),
      life: 3000
    });
  }
}

/**
 * Removes a tab from the deleted tabs list and shows it again.
 * @param xpath - The XPath of the tab to restore.
 */
const undoDeleteTab = (xpath: string) => {
  if (!xpath || xpath.trim() === '') {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.undoDeleteError'),
      life: 3000
    });
    return;
  }

  // Use DeleteCategories to restore the category
  const restoredCategory = deleteCategories?.restoreCategory(xpath);

  if (restoredCategory) {
    toast.add({
      severity: 'success',
      summary: t('common.toast.success'),
      detail: t('config.toast.undoDelete', { category: restoredCategory.translation || restoredCategory.categoryName }),
      life: 3000
    });
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.undoDeleteError'),
      life: 3000
    });
  }
}

/**
 * Switches to a specific category tab based on the provided XPath.
 * If the XPath matches the start of any tab's XPath, it will switch to that tab.
 * @param xPath - The XPath of the category to switch to.
 */
const switchToCategory = (xPath: string) => {
  // Check if xPath is valid
  if (!xPath) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('common.toast.errors.navigationError'),
      life: 3000
    });
    return;
  }

  // Find the index of the tab that matches the categoryXPath, if it's an element path it should start with the xpath of the tab
  const categoryIndex = availableTabs.value.findIndex((tab) => {
    return tab.xpath && xPath.startsWith(tab.xpath);
  });

  if (categoryIndex !== -1) {
    activeTab.value = categoryIndex;
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('common.toast.errors.navigationError'),
      life: 3000
    });
  }
};

/**
 * Switches to a specific tab by its index.
 * @param tabIndex - The index of the tab to switch to.
 */
const switchToTab = (tabIndex: number) => {
  if (tabIndex >= 0 && tabIndex < availableTabs.value.length) {
    activeTab.value = tabIndex;
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('common.toast.navigationError'),
      life: 3000
    });
  }
};

// Expose methods for parent components
defineExpose({
  switchToCategory,
  switchToTab
});
</script>

<template>
  <div>
    <!--If no module is selected -->
    <Message
      v-if="!selectedModuleName"
      severity="info"
      :closable="false"
      class="text-center"
    >
      {{ $t('config.form.noModuleSelected') }}
    </Message>

    <!-- If no tabs are available -->
    <Message
      v-else-if="!availableTabs || availableTabs.length === 0"
      severity="warn"
      :closable="false"
      class="text-center"
    >
      {{ $t('config.form.noElements') }}
    </Message>

    <!-- Tabs with elements -->
    <div v-else class="w-full">
      <h3 v-if="props.showTitle">
        {{ selectedModuleName }}
      </h3>
      <TabView
        v-model:activeIndex="activeTab"
        class="w-full config-tab-view"
        :key="`tabview-${selectedModuleName}`"
      >
        <TabPanel 
          v-for="categoryGroup in availableTabs"
          :key="`${categoryGroup.categoryName}`"
        >
          <template #header>
            <div class="tab-header-inner-content flex items-center justify-between">
              <span>
                {{ categoryGroup.translation || categoryGroup.categoryName }}
              </span>
              <Badge
                v-if="categoryGroup.hidden"
                :value="t('config.toast.deleted')"
                severity="danger" 
                class="ml-2"
                />
            </div>
          </template>
          <div v-if="categoryGroup.hidden">
            <Message
              severity="warn"
              :closable="false"
              class="text-center"
            >
              {{ $t('config.toast.markedForDeletion', { category: categoryGroup.translation || categoryGroup.categoryName }) }}
            </Message>
            <div class="flex justify-content-end gap-2 col-12 p-0 mt-5 pt-3">
              <Button
                v-if="!categoryGroup.categoryName.toLowerCase().includes('settings')"
                id="undo-delete"
                :label="$t('common.actions.undoDelete')"
                type="button"
                class="w-auto"
                severity="warning"
                icon="pi pi-undo"
                @click.stop="undoDeleteTab(categoryGroup.xpath)"
                aria-label="Undo Delete tab"
              />
            </div>
          </div>
          <div v-else>
            <div class="grid user-form mt-1">
              <div class="col-12 p-2">
                <ConfigFormHeadlines
                  v-if="isInitialized(categoryGroup.categoryName)"
                  :headline-groups="categoryGroup.headlineGroups"
                  :category-group="categoryGroup"
                  :open-accordion-tabs="openAccordionTabs[categoryGroup.categoryName]"
                  :get-next-input-id="getNextInputId"
                  :config-values="configParserStore.getConfigValues"
                  @update:activeIndex="(tabs: number[]) => handleAccordionChange(categoryGroup.categoryName, tabs)"
                  @focus:next="handleFocusNext"
                  :profile-settings="profileSettings"
                  :read-only="props.readOnly"
                  :key="`headlines-${categoryGroup.categoryName}`"
                />
              </div>
            </div>
            <div class="flex justify-end gap-2 col-span-12 p-0">
              <Button
                id="delete"
                v-if="props.showDeleteButton && categoryGroup.isDeletable"
                :label="$t('config.form.deleteCategory', { category: categoryGroup.translation || categoryGroup.categoryName })"
                type="button"
                class="w-auto"
                severity="danger"
                icon="pi pi-trash"
                @click.stop="markTabAsDeleted(categoryGroup.xpath, categoryGroup.translation || categoryGroup.categoryName)"
                :loading="deleteLoading"
                aria-label="Delete tab"
              />
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  </div>
</template>

<style scoped>
:deep(.config-tab-view .p-tabview-nav) {
  background: transparent;
  border-bottom: 1px solid hsl(var(--border));
  padding: 0;
}

:deep(.config-tab-view .p-tabview-nav li .p-tabview-nav-link) {
  border: 1px solid #dee2e6;
  border-radius: 6px 6px 0 0;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  margin-right: 2px;
  margin-bottom: -1px;
}

:deep(.config-tab-view .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link) {
  background-color: hsl(var(--background)) !important;
  color: #495057;
  border-bottom-color: #dee2e6;
}

:deep(.config-tab-view .p-tabview-nav li:not(.p-highlight):hover .p-tabview-nav-link) {
  background-color: hsl(var(--background)) !important;
}

:deep(.config-tab-view .p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background-color: #ffffff;
  border-color: #dee2e6;
  border-bottom-color: #ffffff;
  color: var(--primary-color, #3B82F6);
  position: relative;
  z-index: 1;
}

:deep(.config-tab-view .p-tabview-panels) {
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 6px 6px;
}

</style>